import { Checkbox, Loading, Panel } from "@appkit4/react-components";
import { useContext, useEffect, useMemo, useState } from "react";
import CustomTooltipIcon from "../../components/customTooltipIcon/customTooltipIcon";
import toast from "../../components/DismissibleToast";
import { BackButton, NextButton } from "../../components/ProgressNav";
import { Button } from "../../components/ReadonlyAwareInputs";
import RoundedBorder from "../../components/ui/RoundedBorder";
import { AuthContext } from "../../services/AuthProvider";
import {
  canProgressCalculation,
  disableProgressInViewMode,
  getCurrentPeriodIsReadonly,
  getGroupData,
  getPageTooltips,
  getPoaIndex,
  getSelectedPeriod,
  setGroupData,
} from "../../services/GroupContext";
import sqldatabaseService from "../../services/sqldatabase/sqldatabase.service";
import {
  calculateCFD,
  calculateCFDADJ,
  convertAllLcValuesToGBP,
  getIncludedAdjCaps,
  isDifferentPriorApCurrency,
  recalculateAllTotals,
  setBFDADJForEditCFD,
  setBFDToZero,
  setBFDasPrevCFD,
  sortCaps,
} from "../../utils/adjustedCapsUtil";
import CFDDataEditor from "./components/CFDDataEditor";
import {
  calculateIsBFDAdjEditable,
  calculateIsCFDAdjEditable,
} from "./util/EditCFDUtil";

const EditCFD = () => {
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const [freezeCFDAdj, setFreezeCFDAdj] = useState(false);

  const auth = useContext(AuthContext);

  const savedData = getGroupData();
  const poaIndex = getPoaIndex();
  const disableViewModeProgress = disableProgressInViewMode();
  const tooltips = useMemo(() => getPageTooltips("editCFD"), []);

  const [adjustedCaps, setAdjustedCaps] = useState([]);

  const disallowanceDataSections = [
    {
      title: "Brought Forward Disallowances",
      metricGroup: "bfd",
      editable: true,
      validation: "positive",
      col1Data: [
        {
          displayValue: "Non-trading loan relationship amounts",
          key: "bfd_non_trading_loan_relationships",
        },
        {
          displayValue: "Non-trading derivative amounts",
          key: "bfd_non_trading_derivatives",
        },
        {
          displayValue: "Trading loan relationship amounts",
          key: "bfd_trading_loan_relationships",
        },
        {
          displayValue: "Trading derivative amounts",
          key: "bfd_trading_derivatives",
        },
        { displayValue: "Other financing amounts", key: "bfd_other_financing" },
        { displayValue: "Total", key: "bfd_subtotal" },
      ],
    },
    {
      title: "Brought Forward Disallowances Adjustments (Negative)",
      metricGroup: "bfdadj",
      editable: true,
      validation: "negative",
      col1Data: [
        {
          displayValue: "Non-trading loan relationship amounts",
          key: "bfdadj_non_trading_loan_relationships",
          metricType: "nonTrading",
        },
        {
          displayValue: "Non-trading derivative amounts",
          key: "bfdadj_non_trading_derivatives",
          metricType: "nonTrading",
        },
        {
          displayValue: "Trading loan relationship amounts",
          key: "bfdadj_trading_loan_relationships",
          metricType: "trading",
        },
        {
          displayValue: "Trading derivative amounts",
          key: "bfdadj_trading_derivatives",
          metricType: "trading",
        },
        {
          displayValue: "Other financing amounts",
          key: "bfdadj_other_financing",
          metricType: "ofa",
        },
        { displayValue: "Total", key: "bfdadj_subtotal" },
      ],
    },
    {
      title: "Current Period Disallowance",
      editable: false,
      col1Data: [
        {
          displayValue: "Non-trading loan relationship amounts",
          key: "cpd_non_trading_loan_relationships",
        },
        {
          displayValue: "Non-trading derivative amounts",
          key: "cpd_non_trading_derivatives",
        },
        {
          displayValue: "Trading loan relationship amounts",
          key: "cpd_trading_loan_relationships",
        },
        {
          displayValue: "Trading derivative amounts",
          key: "cpd_trading_derivatives",
        },
        { displayValue: "Other financing amounts", key: "cpd_other_financing" },
        { displayValue: "Total", key: "cpd_total" },
      ],
    },
    {
      title: "Current Period Reactivations",
      metricGroup: "cpr",
      editable: false,
      col1Data: [
        {
          displayValue: "Non-trading loan relationship amounts",
          key: "cpr_non_trading_loan_relationships",
        },
        {
          displayValue: "Non-trading derivative amounts",
          key: "cpr_non_trading_derivatives",
        },
        {
          displayValue: "Trading loan relationship amounts",
          key: "cpr_trading_loan_relationships",
        },
        {
          displayValue: "Trading derivative amounts",
          key: "cpr_trading_derivatives",
        },
        { displayValue: "Other financing amounts", key: "cpr_other_financing" },
        { displayValue: "Total", key: "cpr_subtotal" },
      ],
    },
    {
      title: "Carried Forward Disallowances Adjustments (Negative)",
      metricGroup: "cfdadj",
      editable: true,
      validation: "negative",
      col1Data: [
        {
          displayValue: "Non-trading loan relationship amounts",
          key: "cfdadj_non_trading_loan_relationships",
          metricType: "nonTrading",
        },
        {
          displayValue: "Non-trading derivative amounts",
          key: "cfdadj_non_trading_derivatives",
          metricType: "nonTrading",
        },
        {
          displayValue: "Trading loan relationship amounts",
          key: "cfdadj_trading_loan_relationships",
          metricType: "trading",
        },
        {
          displayValue: "Trading derivative amounts",
          key: "cfdadj_trading_derivatives",
          metricType: "trading",
        },
        {
          displayValue: "Other financing amounts",
          key: "cfdadj_other_financing",
          metricType: "ofa",
        },
        { displayValue: "Total", key: "cfdadj_subtotal" },
      ],
    },
    {
      title: "Carried Forward Disallowances",
      editable: false,
      col1Data: [
        {
          displayValue: "Non-trading loan relationship amounts",
          key: "cfd_non_trading_loan_relationships",
        },
        {
          displayValue: "Non-trading derivative amounts",
          key: "cfd_non_trading_derivatives",
        },
        {
          displayValue: "Trading loan relationship amounts",
          key: "cfd_trading_loan_relationships",
        },
        {
          displayValue: "Trading derivative amounts",
          key: "cfd_trading_derivatives",
        },
        { displayValue: "Other financing amounts", key: "cfd_other_financing" },
        { displayValue: "Total", key: "cfd_subtotal" },
      ],
    },
  ];

  useEffect(() => {
    if (!savedData) {
      throw "Something went wrong with fetching your cached data";
    }

    const selectedPoA = getSelectedPeriod();
    const calculatedAdjustedCaps = sortCaps(
      getIncludedAdjCaps(selectedPoA?.adjusted_caps),
    );

    if (auth?.isReadOnlyUser() || getCurrentPeriodIsReadonly()) {
      setIsLoading(false);
      setAdjustedCaps(calculatedAdjustedCaps);
    } else {
      const processedCompanies = [];
      calculatedAdjustedCaps?.forEach((adjustedCap, index) => {
        if (processedCompanies.includes(adjustedCap.company_ID)) {
          // every non-first adjusted cap per company
          const prevAdjustedCap = calculatedAdjustedCaps[index - 1];
          if (isDifferentPriorApCurrency(adjustedCap)) {
            setBFDToZero(adjustedCap);
          } else {
            // set BFD ammount as CFD ammount
            setBFDasPrevCFD(adjustedCap, prevAdjustedCap);
          }
          // set BFDAdj ammount as negative current year BFD
          setBFDADJForEditCFD(adjustedCap, false);
        } else {
          //first adjustedCap for each company
          processedCompanies.push(adjustedCap.company_ID);
        }

        // set CFDAdj as -(BFD + BFDAdj + CPD - CPR)
        calculateCFDADJ(adjustedCap);

        // calculate CFD
        calculateCFD(adjustedCap);

        adjustedCap.isFirstApForCompanyInPoa = ["Start", "StartEnd"].includes(
          adjustedCap.adjCapPosInPoA,
        );

        // if cfdadj is uneditable, reset it to 0
        disallowanceDataSections
          .find((section) => section.metricGroup === "cfdadj")
          ?.col1Data.forEach((metric) => {
            const { metricType, key: lcKey } = metric;
            const gbpKey = `${lcKey}_gbp`;

            const isNonTradingOrOFA =
              metricType === "nonTrading" || metricType === "ofa";
            const isTradingOrOFA =
              metricType === "trading" || metricType === "ofa";
            const isEditable = calculateIsCFDAdjEditable(
              isTradingOrOFA,
              isNonTradingOrOFA,
              adjustedCap,
            );

            if (!isEditable) {
              adjustedCap[lcKey] = 0;
              adjustedCap[gbpKey] = 0;
            }
          });

        // if bfdadj is uneditable, reset it to 0
        disallowanceDataSections
          .find((section) => section.metricGroup === "bfdadj")
          ?.col1Data.forEach((metric) => {
            const { metricType, key: lcKey } = metric;
            const gbpKey = `${lcKey}_gbp`;

            const isNonTradingOrOFA =
              metricType === "nonTrading" || metricType === "ofa";
            const isTradingOrOFA =
              metricType === "trading" || metricType === "ofa";
            const isEditable = calculateIsBFDAdjEditable(
              isTradingOrOFA,
              isNonTradingOrOFA,
              adjustedCap,
            );

            if (!isEditable && !adjustedCap.isFirstApForCompanyInPoa) {
              adjustedCap[lcKey] = 0;
              adjustedCap[gbpKey] = 0;
            }
          });

        recalculateAllTotals(adjustedCap, true);
      });

      setAdjustedCaps(calculatedAdjustedCaps);

      postAdjustdCaps(calculatedAdjustedCaps, true);
    }
  }, []);

  const postAdjustdCaps = (newAdjustedCaps, onLoading = false) => {
    if (!savedData) {
      throw "Something went wrong with fetching your cached data";
    }

    const isDifferentCurrencyFound = newAdjustedCaps?.some((cap) =>
      isDifferentPriorApCurrency(cap),
    );
    const poa = savedData?.periods_of_account[poaIndex];

    const canProgressCalc = canProgressCalculation();
    savedData.periods_of_account[poaIndex] = {
      ...poa,
      adjusted_caps: newAdjustedCaps,
      ...(!onLoading && canProgressCalc),
    };

    if (auth?.isReadOnlyUser()) {
      setIsLoading(false);
      return;
    }
    setIsPosting(true);

    const promise = sqldatabaseService?.postResponse(savedData);
    const onSuccess = () => {
      setGroupData(savedData);
      setIsLoading(false);
      setIsPosting(false);
    };
    const onError = () => {
      setIsLoading(false);
      setIsPosting(false);
    };
    return onLoading
      ? toast.promise(
          promise,
          {
            loading: "Processing Adjusted Caps",
            success: () => {
              setGroupData(savedData);
              setIsLoading(false);
              setIsPosting(false);
              if (isDifferentCurrencyFound) {
                toast.notification(
                  "A currency change between accounting periods has been identified for at least one company. Please enter brought forward disallowances in the new currency manually",
                  { duration: 10000 },
                );
              }
              return;
            },
            error: () => {
              setIsLoading(false);
              setIsPosting(false);
              return "Failed to save Adjusted Caps Calculations";
            },
          },
          {
            success: {
              render: null,
              style: { display: "none" },
              duration: 0,
            },
          },
        )
      : toast.save(promise, onSuccess, onError);
  };

  const save = async () => {
    if (!savedData) {
      throw "Something went wrong with fetching your cached data";
    }

    if (auth?.isReadOnlyUser() || getCurrentPeriodIsReadonly()) {
      return;
    }

    convertAllLcValuesToGBP(adjustedCaps);

    await postAdjustdCaps(adjustedCaps);
  };

  return (
    <div className="ap-container">
      {isLoading ? (
        <Loading
          loadingType="linear"
          indeterminate={true}
          compact={false}
          className="page-loader"
        ></Loading>
      ) : (
        <Panel
          title="Carried Forward Disallowances"
          className="ap-mb-spacing-4"
        >
          <p>
            The disallowances carried forward to the start of the next Period of
            Account may need to be adjusted in the table below. The table
            includes flags highlighting whether the inputs provided to date
            indicate that an adjustment could be required. Only these entries
            are unlocked for editing. As brought forward adjustments were made
            for the first relevant accounting period of a company on the Brought
            Forward Disallowances page, the brought forward amounts for this
            accounting period are not editable.
          </p>
          <p>
            <b>Brought forward adjustments</b>
            <br />
            <p>
              Disallowances may not be be available in an accounting period
              where the condition in s378(4) TIOPA 2010 is met - i.e.
            </p>
            <ul type="a">
              <li>
                Where the trade has become uncommercial or non-statutory
                (Relevant trade amounts only) and/or
                <CustomTooltipIcon
                  size="big"
                  tooltipText={tooltips.tradeUncommercialOrNonStatutory}
                />
              </li>
              <li>
                Where there is a change in ownership of the company (Non-trade
                amounts only).
                <CustomTooltipIcon
                  size="big"
                  tooltipText={tooltips.changeInOwnershipBroughtForward}
                />
              </li>
            </ul>
          </p>
          <p>
            <b>Carried forward adjustments</b>
            <br />
            <p>
              Disallowances may not be be available in a future accounting
              period (i.e. impacting carried forward amounts) where the
              conditions in s378(3), (5) and/or (6) TIOPA 2010 are met - i.e.
            </p>
            <ul type="a">
              <li>
                Where the trade has become small or negligible (relevant trade
                amounts only).
                <CustomTooltipIcon
                  size="big"
                  tooltipText={tooltips.ceasingTrade}
                />
              </li>
              <li>
                Where the Investment business has ceased or become small or
                negligible (Non-trade (investment business) amounts only) and/or
                <CustomTooltipIcon
                  size="big"
                  tooltipText={tooltips.ceasingInvestmentBusiness}
                />
              </li>
              <li>
                Where there is a change in ownership of the company (Non-trade
                amounts only).
                <CustomTooltipIcon
                  size="big"
                  tooltipText={tooltips.changeInOwnershipCarriedForward}
                />
              </li>
            </ul>
          </p>

          <div className="cfd-flex-row">
            <p>Freeze CIR Adjustment Rows</p>
            <Checkbox
              checked={freezeCFDAdj}
              onChange={(value) => {
                setFreezeCFDAdj(value);
              }}
            />
          </div>
          <p className="err">{isError}</p>
          <div
            id="portal"
            style={{ position: "fixed", left: 0, top: 0, zIndex: 9999 }}
          />

          <RoundedBorder>
            <CFDDataEditor
              {...{
                adjustedCaps,
                setAdjustedCaps,
                disallowanceDataSections,
                freezeCFDAdj,
              }}
            />
          </RoundedBorder>

          <div className="container-fluid mt-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <BackButton disabled={isPosting} />
              </div>
              <div className="d-flex gap-2">
                <Button
                  hiddenInReadonly
                  loading={isPosting}
                  onClick={save}
                  kind="secondary"
                >
                  Save
                </Button>
                <NextButton
                  loading={isPosting}
                  disabled={disableViewModeProgress}
                  preNavigation={save}
                />
              </div>
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
};

export default EditCFD;
